import React, { useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

import {
    Box,
    Button,
    Typography,
    useTheme,
    Paper,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Checkbox,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    List,
    ListItem,
    ListItemText,
    IconButton
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import API from "../api/axiosInstance";
import { User, Group, Team, RecordCreationResults } from "../types/interfaces";
import SaveStatusDialog from "./SaveStatusDialog";

const UsersPanel: React.FC = () => {
    const theme = useTheme();
    const [rowData, setRowData] = useState<User[]>([]);
    const [columnDefs, setColumnDefs] = useState<ColDef<User>[]>([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [userGroups, setUserGroups] = useState<Group[]>([]);
    const [userTeams, setUserTeams] = useState<Team[]>([]);
    const [selectedGroupId, setSelectedGroupId] = useState("");
    const [selectedTeamId, setSelectedTeamId] = useState("");
    const [saveStatus, setSaveStatus] = useState<"saving" | "success" | "error" | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [groups, setGroups] = useState<Group[]>([]);
    const [teams, setTeams] = useState<Team[]>([]);
    const [showPassword, setShowPassword] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState("");

    useEffect(() => {
        fetchUsers();
        fetchGroups();
        fetchTeams();

        setColumnDefs([
            { field: "username", headerName: "Username", sortable: true, flex: 1 },
            { field: "fullName", headerName: "Full Name", sortable: true, flex: 1.5 },
            { field: "email", headerName: "Email", sortable: true, flex: 2 },
            {
                field: "active",
                headerName: "Active",
                width: 120,
                cellRenderer: (params: ICellRendererParams) => (
                    <span>{params.value ? "✅ Active" : "❌ Inactive"}</span>
                )
            },
            {
                headerName: "Actions",
                width: 180,
                cellRenderer: (params: ICellRendererParams<User>) => (
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" size="small" onClick={() => handleEdit(params.data!)}>
                            Edit
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleDelete(params.data!)}
                        >
                            Delete
                        </Button>
                    </Stack>
                )
            }
        ]);
    }, []);

    const getHeaders = () => {
        const user = JSON.parse(localStorage.getItem('user') || "{}");
        return {
            headers: {
                authToken: user.user?.authtoken,
                sessionId: user.user?.sessionId,
            },
        };
    };

    const fetchUsers = async () => {
        try {
            const response = await API.get("/security/users", getHeaders());
            setRowData(response.data || []);
        } catch (error) {
            console.error("Error fetching users:", error);
            setRowData([]);
        }
    };

    const renderCheck = (condition: boolean) => (
        <span style={{ marginRight: 6 }}>{condition ? "✅" : "❌"}</span>
    );

    const fetchGroups = async () => {
        try {
            const response = await API.get("/security/groups", getHeaders());
            setGroups(response.data || []);
        } catch (error) {
            console.error("Error fetching groups:", error);
            setGroups([]);
        }
    };

    const fetchTeams = async () => {
        try {
            const response = await API.get("/security/teams", getHeaders());
            setTeams(response.data || []);
        } catch (error) {
            console.error("Error fetching teams:", error);
            setTeams([]);
        }
    };

    const handleAdd = () => {
        const currentUser = JSON.parse(localStorage.getItem('user') || "{}");
        setEditingUser({
            userID: "",
            tenantID: currentUser.user?.tenantID,
            username: "",
            passwordHash: "",
            fullName: "",
            email: "",
            groupID: "",
            active: true,
            resetToken: "",
            phoneNumber: "",
            cellNumber: "",
            companyName: "",
            online: false,
            referralCode: "",
            acceptedTerms: false,
            currentIP: "",
            forceLogoff: false,
            location: "",
            createdBy: currentUser.user?.userID,
            createdAt: new Date().toISOString(),
            updatedBy: "",
            deleted: false,
            deletedBy: "",
        });
        setUserGroups([]);
        setUserTeams([]);
        setIsDialogOpen(true);
    };

    const handleEdit = (user: User) => {
        setEditingUser(user);
        const assignedGroup = groups.find(g => g.groupID === user.groupID);
        const assignedTeam = teams.find(t => t.teamID === user.location);
        setUserGroups(assignedGroup ? [assignedGroup] : []);
        setUserTeams(assignedTeam ? [assignedTeam] : []);
        setIsDialogOpen(true);
    };

    const handleDelete = async (user: User) => {
        if (window.confirm(`Are you sure you want to delete ${user.username}?`)) {
            try {
                await API.delete(`/security/users/${user.userID}`, getHeaders());
                fetchUsers();
            } catch (error) {
                console.error("Error deleting user:", error);
            }
        }
    };

    const handleSave = async () => {
        if (!editingUser) return;
    
        setSaveStatus("saving");
        setSaveError(null);
    
        try {
            if (!editingUser.username || !editingUser.fullName || !editingUser.email) {
                setSaveStatus("error");
                setSaveError("Username, Full Name, and Email are required.");
                return;
            }
    
            const currentUser = JSON.parse(localStorage.getItem('user') || "{}");
    
            // CREATE NEW USER
            if (!editingUser.userID || editingUser.userID === "" || editingUser.userID === "NEW") {
                const createRequest = {
                    tenantID: currentUser.user?.tenantID,
                    username: editingUser.username,
                    password: editingUser.passwordHash || "ChangeMe123!",
                    email: editingUser.email,
                    fullName: editingUser.fullName || "",
                    phoneNumber: editingUser.phoneNumber || "",
                    cellNumber: editingUser.cellNumber || "",
                    location: editingUser.location || null,
                    cellProvider: editingUser.cellProvider || null,
                    timeZoneID: editingUser.timeZoneID || null,
                };
    
                const createResp = await API.post("/security/users", createRequest, getHeaders());
                const result: RecordCreationResults = createResp.data;
    
                if (!result.wasSuccessful || result.newRecordIDs.length === 0) {
                    setSaveStatus("error");
                    setSaveError("Failed to create user.");
                    return;
                }
    
                const newUserId = result.newRecordIDs[0];
    
                // UPDATE FULL USER RECORD
                const updateUser: User = {
                    ...editingUser,
                    userID: newUserId,
                    tenantID: currentUser.user?.tenantID,
                    createdBy: currentUser.user?.userID,
                    createdAt: new Date().toISOString(),
                    updatedBy: currentUser.user?.userID,
                    updatedAt: new Date().toISOString(),
                };
    
                await API.put(`/security/users/${newUserId}`, updateUser, getHeaders());
    
                // ASSIGN GROUPS
                for (const group of userGroups) {
                    await API.post(`/security/groups/${group.groupID}/users`, {
                        userID: newUserId
                    }, getHeaders());
                }
    
                // ASSIGN TEAMS
                for (const team of userTeams) {
                    await API.post(`/security/teams/${team.teamID}/users`, {
                        userID: newUserId
                    }, getHeaders());
                }
    
            } else {
                // EXISTING USER UPDATE
                const updateUser: User = {
                    ...editingUser,
                    updatedBy: currentUser.user?.userID,
                    updatedAt: new Date().toISOString(),
                };
    
                await API.put(`/security/users/${editingUser.userID}`, updateUser, getHeaders());
    
                // Optional: Reassign teams/groups if editable here
            }
    
            setSaveStatus("success");
            setTimeout(() => {
                setSaveStatus(null);
                setIsDialogOpen(false);
                fetchUsers();
            }, 1000);
    
        } catch (err: any) {
            console.error("Save failed", err);
            setSaveStatus("error");
            
            // Try to extract error from API response
            const apiErrors = err?.response?.data?.errors;
            if (Array.isArray(apiErrors) && apiErrors.length > 0) {
                setSaveError(apiErrors[0]);
            } else if (err?.response?.data?.message) {
                setSaveError(err.response.data.message);
            } else {
                setSaveError(err?.message || "Unknown error occurred.");
            }
        }
    };
    

    const handleAddGroup = () => {
        const group = groups.find(g => g.groupID === selectedGroupId);
        if (group && !userGroups.some(g => g.groupID === group.groupID)) {
            setUserGroups(prev => [...prev, group]);
        }
        setSelectedGroupId("");
    };

    const handleRemoveGroup = (id: string) => {
        setUserGroups(prev => prev.filter(g => g.groupID !== id));
    };

    const handleAddTeam = () => {
        const team = teams.find(t => t.teamID === selectedTeamId);
        if (team && !userTeams.some(t => t.teamID === team.teamID)) {
            setUserTeams(prev => [...prev, team]);
        }
        setSelectedTeamId("");
    };

    const handleRemoveTeam = (id: string) => {
        setUserTeams(prev => prev.filter(t => t.teamID !== id));
    };

    return (
        <Box>
            <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Users</Typography>
                <Button variant="contained" onClick={handleAdd}>+ Add User</Button>
            </Box>

            {rowData.length === 0 ? (
                <Typography variant="body2" color="textSecondary" textAlign="center">
                    No users added yet.
                </Typography>
            ) : columnDefs.length === 0 ? (
                <Typography variant="body2" color="textSecondary" textAlign="center">
                    Loading user table...
                </Typography>
            ) : (
                <Paper elevation={2} sx={{ p: 1, backgroundColor: theme.palette.background.paper, borderRadius: 2, overflow: "hidden" }}>
                    <Box className={`ag-theme-${theme.palette.mode === "dark" ? "alpine-dark" : "alpine"}`} sx={{ height: "60vh", width: "100%" }}>
                        <AgGridReact columnDefs={columnDefs} rowData={rowData} rowSelection="single" />
                    </Box>
                </Paper>
            )}

            <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingUser?.userID ? "Edit User" : "New User"}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} mt={1}>
                        <TextField label="Username" fullWidth value={editingUser?.username || ""} onChange={(e) => setEditingUser((prev) => ({ ...prev!, username: e.target.value }))} />
                        <TextField label="Full Name" fullWidth value={editingUser?.fullName || ""} onChange={(e) => setEditingUser((prev) => ({ ...prev!, fullName: e.target.value }))} />
                        <TextField label="Email" fullWidth type="email" value={editingUser?.email || ""} onChange={(e) => setEditingUser((prev) => ({ ...prev!, email: e.target.value }))} />

                        {!editingUser?.userID && (
    <>
        <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            fullWidth
            value={editingUser?.passwordHash || ""}
            onChange={(e) =>
                setEditingUser((prev) => ({ ...prev!, passwordHash: e.target.value }))
            }
        />
        <TextField
            label="Confirm Password"
            type={showPassword ? "text" : "password"}
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={
                confirmPassword.length > 0 &&
                confirmPassword !== (editingUser?.passwordHash || "")
            }
            helperText={
                confirmPassword.length > 0 &&
                confirmPassword !== (editingUser?.passwordHash || "")
                    ? "Passwords do not match"
                    : ""
            }
        />
        <FormControlLabel
            control={
                <Checkbox
                    checked={showPassword}
                    onChange={(e) => setShowPassword(e.target.checked)}
                />
            }
            label="Show Password"
        />
        <Box sx={{ pl: 1 }}>
            <Typography variant="subtitle2">Password must contain:</Typography>
            <Stack spacing={0.5} mt={1} sx={{ pl: 2 }}>
                <Typography variant="body2">
                    {renderCheck((editingUser?.passwordHash || "").length >= 8)} At least 8 characters
                </Typography>
                <Typography variant="body2">
                    {renderCheck(/[A-Z]/.test(editingUser?.passwordHash || ""))} 1 uppercase letter
                </Typography>
                <Typography variant="body2">
                    {renderCheck(/[a-z]/.test(editingUser?.passwordHash || ""))} 1 lowercase letter
                </Typography>
                <Typography variant="body2">
                    {renderCheck(/\d/.test(editingUser?.passwordHash || ""))} 1 number
                </Typography>
                <Typography variant="body2">
                    {renderCheck(/[^A-Za-z0-9]/.test(editingUser?.passwordHash || ""))} 1 special character
                </Typography>
            </Stack>
        </Box>
    </>
)}


                        {/* Group Assignment */}
                        <Typography variant="subtitle2">Groups</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Select value={selectedGroupId} onChange={(e) => setSelectedGroupId(e.target.value)} displayEmpty fullWidth>
                                <MenuItem value="" disabled>Select group to add</MenuItem>
                                {groups.map(g => (
                                    <MenuItem key={g.groupID} value={g.groupID}>{g.groupName}</MenuItem>
                                ))}
                            </Select>
                            <Button onClick={handleAddGroup}>Add</Button>
                        </Stack>
                        <List dense>
                            {userGroups.map(g => (
                                <ListItem key={g.groupID} secondaryAction={<IconButton onClick={() => handleRemoveGroup(g.groupID)}><DeleteIcon /></IconButton>}>
                                    <ListItemText primary={g.groupName} />
                                </ListItem>
                            ))}
                        </List>

                        {/* Team Assignment */}
                        <Typography variant="subtitle2">Teams</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Select value={selectedTeamId} onChange={(e) => setSelectedTeamId(e.target.value)} displayEmpty fullWidth>
                                <MenuItem value="" disabled>Select team to add</MenuItem>
                                {teams.map(t => (
                                    <MenuItem key={t.teamID} value={t.teamID}>{t.teamName}</MenuItem>
                                ))}
                            </Select>
                            <Button onClick={handleAddTeam}>Add</Button>
                        </Stack>
                        <List dense>
                            {userTeams.map(t => (
                                <ListItem key={t.teamID} secondaryAction={<IconButton onClick={() => handleRemoveTeam(t.teamID)}><DeleteIcon /></IconButton>}>
                                    <ListItemText primary={t.teamName} />
                                </ListItem>
                            ))}
                        </List>

                        <FormControlLabel control={<Checkbox checked={editingUser?.active || false} onChange={(e) => setEditingUser((prev) => ({ ...prev!, active: e.target.checked }))} />} label="Active" />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>Save</Button>
                </DialogActions>
            </Dialog>

            <SaveStatusDialog open={!!saveStatus} status={saveStatus || "saving"} message={saveError || ""} onClose={() => setSaveStatus(null)} />
        </Box>
    );
};

export default UsersPanel;
